/**
 * Patient Verification Step - Redesigned
 * 
 * Addresses Client Feedback:
 * - Auto-populated patient information (no manual entry)
 * - Read-only verification interface  
 * - Focus on safety confirmation
 * - Quick access to medical history
 * - Mandatory identity confirmation
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Alert,
    Button,
    Checkbox,
    Space,
    Tag,
    Descriptions,
    Modal,
    List,
    Divider
} from 'antd';
import {
    UserOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    SafetyCertificateOutlined,
    MedicineBoxOutlined,
    HistoryOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { getCurrentPhysicianName, getCurrentUserId } from '../../../utils/authUtils';

const PatientVerificationStep = ({ patient, workflowData, onVerified, loading }) => {
    console.log('[PatientVerificationStep] props:', { patient, workflowData, onVerified, loading });
    const [patientConfirmed, setPatientConfirmed] = useState(false);
    const [showMedicalHistory, setShowMedicalHistory] = useState(false);
    const [showCurrentMedications, setShowCurrentMedications] = useState(false);

    // Auto-populate verification when patient data is available
    useEffect(() => {
        console.log('[PatientVerificationStep] useEffect workflowData:', workflowData);
        if (workflowData?.patient_context?.verified) {
            setPatientConfirmed(true);
        }
    }, [workflowData]);

    const handlePatientConfirmation = (confirmed) => {
        setPatientConfirmed(confirmed);
        
        if (confirmed) {
            // Create verification data
            const verificationData = {
                ...workflowData.patient_context,
                verified: true,
                verified_at: new Date().toISOString(),
                verified_by: getCurrentUserId() || getCurrentPhysicianName()
            };
            
            onVerified(verificationData);
        }
    };

    const renderCriticalAlerts = () => {
        console.log('[PatientVerificationStep] renderCriticalAlerts patient:', patient);
        const alerts = [];
        
        // Allergy alerts
        if (patient?.allergies?.length > 0) {
            alerts.push({
                type: 'error',
                title: 'KNOWN ALLERGIES',
                description: patient.allergies.map((allergy, index) => (
                    <Tag key={`allergy-${index}`} color="red" style={{ margin: '2px' }}>
                        {typeof allergy === 'object' ? allergy.name || allergy.allergen : allergy}
                    </Tag>
                ))
            });
        }

        // Current medication alerts
        if (patient?.current_medications?.length > 0) {
            alerts.push({
                type: 'warning',
                title: 'CURRENT MEDICATIONS',
                description: (
                    <div>
                        <div>{patient.current_medications.length} active medication(s)</div>
                        <Button 
                            type="link" 
                            size="small" 
                            icon={<EyeOutlined />}
                            onClick={() => setShowCurrentMedications(true)}
                        >
                            View Details
                        </Button>
                    </div>
                )
            });
        }

        // Medical condition alerts
        if (patient?.medical_conditions?.length > 0) {
            alerts.push({
                type: 'info',
                title: 'MEDICAL CONDITIONS',
                description: patient.medical_conditions.slice(0, 3).map((condition, index) => (
                    <Tag key={`condition-${index}`} color="orange" style={{ margin: '2px' }}>
                        {typeof condition === 'object' ? condition.name || condition.condition : condition}
                    </Tag>
                ))
            });
        }

        return alerts.map((alert, index) => (
            <Alert
                key={`alert-${index}-${alert.type}`}
                message={alert.title}
                description={alert.description}
                type={alert.type}
                showIcon
                style={{ marginBottom: 8 }}
            />
        ));
    };

    const renderPatientSummary = () => (
        <Card 
            title={
                <Space>
                    <UserOutlined />
                    Patient Identity Verification
                </Space>
            }
            extra={
                <Space>
                    <Button 
                        type="ghost" 
                        icon={<HistoryOutlined />}
                        onClick={() => setShowMedicalHistory(true)}
                    >
                        Medical History
                    </Button>
                </Space>
            }
        >
            <Alert
                message="Please confirm you are prescribing for the correct patient"
                description="Review the patient information below and confirm identity before proceeding"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Full Name" span={1}>
                    <strong>{patient?.full_name}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Medical Record Number" span={1}>
                    <strong>{patient?.mrn}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Date of Birth" span={1}>
                    {patient?.date_of_birth ? moment(patient.date_of_birth).format('DD/MM/YYYY') : 'Not recorded'}
                </Descriptions.Item>
                <Descriptions.Item label="Age" span={1}>
                    {patient?.age} years
                </Descriptions.Item>
                <Descriptions.Item label="Gender" span={1}>
                    {patient?.gender || 'Not specified'}
                </Descriptions.Item>
                <Descriptions.Item label="Status" span={1}>
                    <Tag color="green">Active Patient</Tag>
                </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Patient Confirmation Checkbox */}
            <Row justify="center" style={{ marginTop: 16 }}>
                <Col>
                    <Checkbox
                        checked={patientConfirmed}
                        onChange={(e) => handlePatientConfirmation(e.target.checked)}
                        style={{ fontSize: '16px' }}
                    >
                        <strong>I confirm this is the correct patient for this prescription</strong>
                    </Checkbox>
                </Col>
            </Row>

            {patientConfirmed && (
                <Alert
                    message="Patient identity confirmed"
                    description="You may proceed to medication selection"
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                    style={{ marginTop: 16 }}
                />
            )}
        </Card>
    );

    const renderMedicalHistoryModal = () => (
        <Modal
            title={`Medical History - ${patient?.full_name}`}
            open={showMedicalHistory}
            onCancel={() => setShowMedicalHistory(false)}
            footer={[
                <Button key="close" onClick={() => setShowMedicalHistory(false)}>
                    Close
                </Button>
            ]}
            width={800}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                {/* Allergies */}
                <Card size="small" title="Known Allergies">
                    {patient?.allergies?.length > 0 ? (
                        patient.allergies.map((allergy, index) => (
                            <Tag key={`modal-allergy-${index}`} color="red" style={{ margin: '4px' }}>
                                {typeof allergy === 'object' ? allergy.name || allergy.allergen : allergy}
                            </Tag>
                        ))
                    ) : (
                        <div style={{ color: '#999' }}>No known allergies recorded</div>
                    )}
                </Card>

                {/* Medical Conditions */}
                <Card size="small" title="Medical Conditions">
                    {patient?.medical_conditions?.length > 0 ? (
                        patient.medical_conditions.map((condition, index) => (
                            <Tag key={`modal-condition-${index}`} color="orange" style={{ margin: '4px' }}>
                                {typeof condition === 'object' ? condition.name || condition.condition : condition}
                            </Tag>
                        ))
                    ) : (
                        <div style={{ color: '#999' }}>No medical conditions recorded</div>
                    )}
                </Card>

                {/* Previous Medications */}
                <Card size="small" title="Medication History">
                    <div style={{ color: '#999' }}>
                        Previous medication history will be displayed here
                    </div>
                </Card>
            </Space>
        </Modal>
    );

    const renderCurrentMedicationsModal = () => (
        <Modal
            title={`Current Medications - ${patient?.full_name}`}
            open={showCurrentMedications}
            onCancel={() => setShowCurrentMedications(false)}
            footer={[
                <Button key="close" onClick={() => setShowCurrentMedications(false)}>
                    Close
                </Button>
            ]}
            width={600}
        >
            {patient?.current_medications?.length > 0 ? (
                <List
                    dataSource={patient.current_medications}
                    renderItem={(medication, index) => (
                        <List.Item key={`medication-${index}`}>
                            <List.Item.Meta
                                avatar={<MedicineBoxOutlined style={{ color: '#1890ff' }} />}
                                title={typeof medication === 'object' ? medication.name || medication.medication : medication}
                                description="Active medication"
                            />
                        </List.Item>
                    )}
                />
            ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    No current medications recorded
                </div>
            )}
        </Modal>
    );

    return (
        <div className="patient-verification-step">
            <Row gutter={[16, 16]}>
                {/* Main Patient Information */}
                <Col xs={24} lg={16}>
                    {renderPatientSummary()}
                </Col>

                {/* Critical Alerts Panel */}
                <Col xs={24} lg={8}>
                    <Card 
                        title={
                            <Space>
                                <SafetyCertificateOutlined />
                                Critical Safety Information
                            </Space>
                        }
                        size="small"
                    >
                        {renderCriticalAlerts()}
                        
                        {patient?.allergies?.length === 0 && 
                         patient?.current_medications?.length === 0 && 
                         patient?.medical_conditions?.length === 0 && (
                            <Alert
                                message="No Critical Alerts"
                                description="No allergies, current medications, or critical conditions on record"
                                type="success"
                                showIcon
                            />
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Action Buttons */}
            <Row justify="end" style={{ marginTop: 24 }}>
                <Col>
                    <Button 
                        type="primary" 
                        size="large"
                        disabled={!patientConfirmed}
                        loading={loading}
                        icon={<CheckCircleOutlined />}
                        onClick={() => {
                            if (patientConfirmed && onVerified) {
                                // Create verification data and proceed to next step
                                const verificationData = {
                                    ...workflowData?.patient_context,
                                    verified: true,
                                    verified_at: new Date().toISOString(),
                                    verified_by: getCurrentUserId() || getCurrentPhysicianName()
                                };
                                onVerified(verificationData);
                            }
                        }}
                    >
                        Verified - Proceed to Medication Selection
                    </Button>
                </Col>
            </Row>

            {/* Modals */}
            {renderMedicalHistoryModal()}
            {renderCurrentMedicationsModal()}
        </div>
    );
};

export default PatientVerificationStep;